import { useState, useEffect } from "react";
import { sellerService } from "../../services/enhancedCrudService";
import { toast } from "react-toastify";

const SellerReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [currentSellerId, setCurrentSellerId] = useState(null);
  const [responseData, setResponseData] = useState(null);

  const fetchSellerProfile = async () => {
    try {
      const user = sessionStorage.getItem("user");
      const profile = JSON.parse(user);
      if (profile && profile.id) {
        setCurrentSellerId(profile.seller_profile.id);
        fetchReviews(profile.seller_profile.id);
      }
    } catch (error) {
      console.error("Error fetching seller profile:", error);
      toast.error("Failed to load seller profile");
      setLoading(false);
    }
  };

  const fetchReviews = async (sellerId) => {
    try {
      setLoading(true);
      const response = await sellerService.getSellerReviews(sellerId);
      console.log("API Response:", response);
      setResponseData(response);
      if (response && typeof response === "object") {
        let reviewData = [];
        if (Array.isArray(response.reviews)) {
          reviewData = response.reviews;
        } else if (Array.isArray(response)) {
          reviewData = response;
        } else if (response?.results && Array.isArray(response.results)) {
          reviewData = response.results;
        }
        setReviews(reviewData);
        console.log("Reviews set to:", reviewData);
      } else {
        setReviews([]);
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Failed to load reviews");
      setReviews([]);
      setResponseData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this review? This action cannot be undone."
      )
    ) {
      try {
        setDeleting(true);
        await sellerService.deleteReview(reviewId);
        toast.success("Review deleted successfully");
        if (currentSellerId) {
          fetchReviews(currentSellerId);
        }
      } catch (error) {
        console.error("Error deleting review:", error);
        toast.error("Failed to delete review");
      } finally {
        setDeleting(false);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star text-warning"></i>);
    }
    if (hasHalfStar) {
      stars.push(
        <i key="half" className="fas fa-star-half-alt text-warning"></i>
      );
    }
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <i key={`empty-${i}`} className="far fa-star text-warning"></i>
      );
    }
    return stars;
  };

  useEffect(() => {
    fetchSellerProfile();
  }, []);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">Seller Reviews</h4>
            <div className="mt-4 pt-1">
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-comments text-primary me-2"></i>
                    <span className="fw-semibold text-dark">
                      Total Reviews:
                    </span>
                    <span className="badge bg-primary ms-2 fs-6">
                      {reviews.length}
                    </span>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center justify-content-end">
                    <span className="fw-semibold text-dark me-2">
                      Average Rating:
                    </span>
                    <div className="d-flex align-items-center">
                      <div className="me-2">
                        {renderStars(responseData?.average_rating || 0)}
                      </div>
                      <span className="badge bg-warning fs-6">
                        {responseData?.average_rating || 0}/5
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>{" "}
          </div>
          <div className="card-body">
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                    <th>Sr.No</th>
                      <th>Buyer</th>
                      <th>Rating</th>
                      <th>Comment</th>
                      <th>Date</th>
                      {/* <th>Actions</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {reviews.map((review,index) => (
                      <tr key={index}>
                      <td>{index + 1}</td>
                        <td>
                          <div className="mb-0">
                            {review.buyer_firm_name || "Anonymous Buyer"}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="me-2">
                              {renderStars(review.rating || 0)}
                            </div>
                            <span className="badge bg-primary">
                              {review.rating || 0}/5
                            </span>
                          </div>
                        </td>
                        <td>
                          <div
                            className="text-wrap"
                            style={{ maxWidth: "300px" }}
                          >
                            {review.comment || "No comment provided"}
                          </div>
                        </td>
                        <td>
                          <small className="text-muted">
                            {formatDate(review.created_at || review.createdAt)}
                          </small>
                        </td>
                        {/* <td>
                          <i
                            className="pointer fas fa-trash text-danger fa-lg"
                            onClick={() => handleDeleteReview(review.id)}
                          ></i>
                        </td> */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerReviews;
